/**
 * Shared step content for the setup flow.
 * Renders the same steps for both full-screen (setupPage) and modal (performanceOnboardingBanner).
 * Receives all state and callbacks via the `flow` and `refs` props.
 */
import {Box} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import {
  csvAmountSignConventionFromTypeKey,
  deriveAccountTypeKeyFromApi,
  plaidTypeSubtypeFromTypeKey,
} from "../../../utils/bankAccountTypeUtils";
import {submitBankConnected} from "../../../api/customerSetup";
import {deletePlaidItem, fakePlaidImport, updatePlaidItem} from "../../../api/plaid";
import LinkPlaidButton from "../../plaid/linkPlaidMUI";
import {PracticeSetupFormPart1, PracticeSetupFormPart2, PracticeSetupFormPart3} from "./practiceSetupPart1";
import {BankAccountSetup, BankAccountSetupSkeleton, BankLinkInfo} from "./practiceSetupPart2";
import MarketingEffortsForm, {ReviewPerformance, TeamOverview, WeeklyStatsForm} from "./practiceSetupPart3";
import {BookACall} from "./bookACall";
import SuccessOverlay from "./SuccessOverlay";
import SetupSuccessStep from "./SetupSuccessStep";
import {formatCsvImportError} from "../utils";

export default function SetupFlowContent({ flow, refs, profile, onProceedToDemo }) {
  const {
    stepperStep,
    part1Step,
    part2Step,
    part3Step,
    inputData,
    setInputData,
    formErrors,
    setFormErrors,
    formErrorsMode,
    setFormErrorsMode,
    formErrorsTeam,
    setFormErrorsTeam,
    formErrorsWeeklyStats,
    setFormErrorsWeeklyStats,
    customer,
    draftAccountSetupRows,
    setDraftAccountSetupRows,
    draftConnectRowId,
    setDraftConnectRowId,
    pendingDraftConnectMethod,
    setPendingDraftConnectMethod,
    plaidItems,
    checkingPlaid,
    plaidLinking,
    showSuccess,
    setShowSuccess,
    creatingCsvAccount,
    setCsvSnackbarMsg,
    setCsvSnackbarSeverity,
    setCsvSnackbarOpen,
    setPart2Step,
    refreshPlaidItemList,
    setPlaidItems,
    setCreatingCsvAccount,
    setPlaidLinking,
  } = flow;

  const { bookACallRef, connectSnapshotRef, draftCsvFilePickerRef, draftCsvTargetRowIdRef, draftCsvFilesByRowIdRef } = refs || {};

  const handleDeleteRow = (row) => {
    const rowId = row?.id || null;
    if (!rowId) return;
    setDraftAccountSetupRows((prev) => (Array.isArray(prev) ? prev : []).filter((r) => r?.id !== rowId));
    if (row?.sourcePlaidItemId && customer) {
      setPlaidItems((prev) => (Array.isArray(prev) ? prev : []).filter((p) => p?.id !== row.sourcePlaidItemId));
      void (async () => {
        try {
          await deletePlaidItem(customer, row.sourcePlaidItemId);
          await refreshPlaidItemList(customer);
        } catch (err) {
          console.error("Error deleting plaid item:", err);
          setCsvSnackbarMsg("Failed to delete account. Please try again.");
          setCsvSnackbarSeverity("error");
          setCsvSnackbarOpen(true);
          await refreshPlaidItemList(customer);
          setDraftAccountSetupRows((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            if (list.some((r) => r?.id === rowId)) return list;
            return [...list, row];
          });
        }
      })();
    }
  };

  const handleDisconnectRow = (row) => {
    const rowId = row?.id || null;
    if (!rowId) return;
    const previousSourcePlaidItemId = row?.sourcePlaidItemId || null;
    setDraftAccountSetupRows((prev) =>
      (Array.isArray(prev) ? prev : []).map((r) =>
        r?.id === rowId ? { ...r, isConnected: false, connectionMethod: null, sourcePlaidItemId: null } : r
      )
    );
    if (previousSourcePlaidItemId && customer) {
      void (async () => {
        try {
          await deletePlaidItem(customer, previousSourcePlaidItemId);
          await refreshPlaidItemList(customer);
        } catch (err) {
          console.error("Error removing connection:", err);
          setCsvSnackbarMsg("Failed removing connection. Please try again.");
          setCsvSnackbarSeverity("error");
          setCsvSnackbarOpen(true);
          await refreshPlaidItemList(customer);
          setDraftAccountSetupRows((prev) =>
            (Array.isArray(prev) ? prev : []).map((r) =>
              r?.id === rowId ? { ...r, isConnected: true, connectionMethod: row.connectionMethod, sourcePlaidItemId: previousSourcePlaidItemId } : r
            )
          );
        }
      })();
    }
  };

  const handleConnectData = (row, options) => {
    setPart2Step(3);
    if (options?.method !== "plaid" && options?.method !== "csv") return;
    const rowId = row?.id || null;
    setDraftConnectRowId(rowId);
    if (options.method === "csv" && draftCsvTargetRowIdRef) draftCsvTargetRowIdRef.current = rowId;
    if (connectSnapshotRef) {
      connectSnapshotRef.current = {
        rowId,
        method: options.method,
        bankName: (row?.bankName || "").trim(),
        accountName: (row?.accountName || "").trim(),
        plaidItemIds: (Array.isArray(plaidItems) ? plaidItems : []).map((p) => p?.id).filter(Boolean),
      };
    }
    setPendingDraftConnectMethod({ method: options.method, key: Date.now() });
    if (options.method === "csv" && draftCsvFilePickerRef?.current) {
      try {
        draftCsvFilePickerRef.current.value = "";
        draftCsvFilePickerRef.current.click();
      } catch (err) {
        console.warn("Failed opening CSV file picker:", err);
      }
    }
  };

  return (
    <>
      {part1Step === 0 && stepperStep === 0 && (
        <Box component="form" autoComplete="on">
          <PracticeSetupFormPart1
            data={inputData}
            setData={setInputData}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
          />
        </Box>
      )}
      {part1Step === 1 && stepperStep === 0 && (
        <PracticeSetupFormPart2
          data={inputData}
          setData={setInputData}
          formErrors={formErrorsMode}
          setFormErrors={setFormErrorsMode}
        />
      )}
      {part1Step === 2 && stepperStep === 0 && (
        <PracticeSetupFormPart3 data={inputData} setData={setInputData} />
      )}
      {stepperStep === 1 && (
        <>
          <SuccessOverlay
            open={showSuccess}
            onClose={() => setShowSuccess(false)}
            setStepperStep={flow.setStepperStep}
          />
          {part2Step === 0 ? (
            <BankLinkInfo />
          ) : part2Step === 3 ? (
            <>
              <Box
                sx={{
                  position: "fixed",
                  left: "-10000px",
                  top: "-10000px",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              >
                <LinkPlaidButton
                  customer_id={customer}
                  bankLinked={false}
                  checkingPlaid={checkingPlaid}
                  setStepperStep={() => {}}
                  labelWhenUnlinked="Connect Via Plaid"
                  labelWhenLinked="Connected"
                  disableAutoAdvance={true}
                  startIcon={<LinkIcon />}
                  buttonSx={{}}
                  autoOpenKey={pendingDraftConnectMethod?.method === "plaid" ? pendingDraftConnectMethod.key : null}
                  onExchangeStart={() => flow.setPlaidLinking(true)}
                  onBankLinkedChange={(linked) => {
                    if (!linked) return;
                    const snapshot = connectSnapshotRef?.current;
                    const previousIds = new Set(snapshot?.plaidItemIds || []);
                    setShowSuccess(true);
                    submitBankConnected(inputData, customer);
                    setPendingDraftConnectMethod(null);

                    void (async () => {
                      try {
                        const nextItems = await refreshPlaidItemList(customer);
                        const newItem = (Array.isArray(nextItems) ? nextItems : []).find(
                          (item) => item?.id && !previousIds.has(item.id)
                        );
                        const plaidItemId = newItem?.id || null;
                        const targetRowId = snapshot?.rowId || draftConnectRowId;
                        const bankAccounts = newItem?.bankaccount_set || [];
                        const desiredBankName = (snapshot?.bankName || "").trim();

                        if (targetRowId) {
                          setDraftAccountSetupRows((prev) => {
                            const list = Array.isArray(prev) ? prev : [];
                            if (bankAccounts.length > 0) {
                              const without = list.filter((r) => r?.id !== targetRowId);
                              const newRows = bankAccounts.map((account) => ({
                                id: `plaid_${plaidItemId}_${account.id}`,
                                bankName: desiredBankName || newItem?.name || "",
                                accountName: account.official_name || account.name || "",
                                typeKey: deriveAccountTypeKeyFromApi({ accountType: account.type, accountSubtype: account.subtype }) || "checking",
                                isConnected: true,
                                connectionMethod: "plaid",
                                sourcePlaidItemId: plaidItemId,
                                sourceAccountId: account.id,
                              }));
                              return [...without, ...newRows];
                            }
                            return list.map((r) =>
                              r?.id === targetRowId
                                ? { ...r, isConnected: true, connectionMethod: "plaid", sourcePlaidItemId: plaidItemId }
                                : r
                            );
                          });
                        }

                        if (plaidItemId && desiredBankName) {
                          try {
                            await updatePlaidItem(customer, plaidItemId, { name: desiredBankName });
                            await refreshPlaidItemList(customer);
                          } catch (err) {
                            console.error("Failed updating bank name:", err);
                          }
                        }
                      } catch (err) {
                        console.error("Failed processing Plaid connection:", err);
                        setCsvSnackbarMsg("Failed linking bank. Please refresh and try again.");
                        setCsvSnackbarSeverity("error");
                        setCsvSnackbarOpen(true);
                      } finally {
                        if (connectSnapshotRef) connectSnapshotRef.current = null;
                        setDraftConnectRowId(null);
                        setPlaidLinking?.(false);
                      }
                    })();
                  }}
                />
                <input
                  ref={draftCsvFilePickerRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    const targetRowId = draftCsvTargetRowIdRef?.current;
                    if (file && targetRowId) {
                      if (draftCsvFilesByRowIdRef) draftCsvFilesByRowIdRef.current = { ...(draftCsvFilesByRowIdRef.current || {}), [targetRowId]: file };
                    }
                    if (draftCsvTargetRowIdRef) draftCsvTargetRowIdRef.current = null;
                    setPendingDraftConnectMethod(null);

                    if (!file || !targetRowId) return;

                    const targetRow = (Array.isArray(draftAccountSetupRows) ? draftAccountSetupRows : []).find((r) => r?.id === targetRowId);
                    const bankName = (targetRow?.bankName || connectSnapshotRef?.current?.bankName || "").trim();
                    const accountName = (targetRow?.accountName || connectSnapshotRef?.current?.accountName || "").trim();
                    if (!bankName) {
                      setCsvSnackbarMsg("Bank name is required before uploading a CSV.");
                      setCsvSnackbarSeverity("error");
                      setCsvSnackbarOpen(true);
                      return;
                    }
                    if (!accountName) {
                      setCsvSnackbarMsg("Account name is required before uploading a CSV.");
                      setCsvSnackbarSeverity("error");
                      setCsvSnackbarOpen(true);
                      return;
                    }
                    const resolvedCustomerId = customer || null;
                    if (!resolvedCustomerId) {
                      setCsvSnackbarMsg("Missing customer ID. Please refresh and try again.");
                      setCsvSnackbarSeverity("error");
                      setCsvSnackbarOpen(true);
                      return;
                    }

                    void (async () => {
                      if (setCreatingCsvAccount) setCreatingCsvAccount(true);
                      try {
                        const snapshot = connectSnapshotRef?.current;
                        const previousIds = new Set(snapshot?.plaidItemIds || []);

                        const fd = new FormData();
                        fd.append("bank_name", bankName);
                        fd.append("account_name", accountName);
                        fd.append("file", file);
                        fd.append("customer_id", resolvedCustomerId);
                        fd.append("csv_amount_sign_convention", csvAmountSignConventionFromTypeKey(targetRow?.typeKey));
                        const { type: accountType, subtype: accountSubtype } = plaidTypeSubtypeFromTypeKey(targetRow?.typeKey || "checking");
                        fd.append("account_type", accountType);
                        fd.append("account_subtype", accountSubtype);

                        const res = await fakePlaidImport(fd);
                        if (res?.status !== 202) throw new Error("Unexpected response from server.");

                        setDraftAccountSetupRows((prev) =>
                          (Array.isArray(prev) ? prev : []).map((r) =>
                            r?.id === targetRowId ? { ...r, isConnected: true, connectionMethod: "csv" } : r
                          )
                        );

                        const nextItems = await refreshPlaidItemList(customer);
                        const newItem = (Array.isArray(nextItems) ? nextItems : []).find((item) => item?.id && !previousIds.has(item.id));
                        setDraftAccountSetupRows((prev) =>
                          (Array.isArray(prev) ? prev : []).map((r) =>
                            r?.id === targetRowId ? { ...r, sourcePlaidItemId: newItem?.id || null } : r
                          )
                        );

                        if (connectSnapshotRef) connectSnapshotRef.current = null;
                        setCsvSnackbarMsg("CSV import queued.");
                        setCsvSnackbarSeverity("success");
                        setCsvSnackbarOpen(true);
                      } catch (err) {
                        console.error("Draft CSV import failed:", err);
                        setCsvSnackbarMsg(formatCsvImportError(err));
                        setCsvSnackbarSeverity("error");
                        setCsvSnackbarOpen(true);
                      } finally {
                        if (setCreatingCsvAccount) setCreatingCsvAccount(false);
                      }
                    })();
                  }}
                />
              </Box>

              {(plaidLinking || creatingCsvAccount) ? (
                <BankAccountSetupSkeleton rows={draftAccountSetupRows.length || 2} />
              ) : (
                <BankAccountSetup
                  rows={draftAccountSetupRows}
                  setRows={setDraftAccountSetupRows}
                  onDeleteRow={handleDeleteRow}
                  onDisconnectRow={handleDisconnectRow}
                  onConnectData={handleConnectData}
                />
              )}
            </>
          ) : null}
        </>
      )}
      {part3Step === 0 && stepperStep === 2 && (
        <TeamOverview
          inputData={inputData}
          setInputData={setInputData}
          formErrors={formErrorsTeam}
          setFormErrors={setFormErrorsTeam}
        />
      )}
      {part3Step === 1 && stepperStep === 2 && (
        <WeeklyStatsForm
          data={inputData}
          setData={setInputData}
          formErrors={formErrorsWeeklyStats}
          setFormErrors={setFormErrorsWeeklyStats}
        />
      )}
      {part3Step === 2 && stepperStep === 2 && (
        <MarketingEffortsForm data={inputData} setData={setInputData} />
      )}
      {part3Step === 3 && stepperStep === 2 && (
        <ReviewPerformance inputData={inputData} setInputData={setInputData} />
      )}
      {stepperStep === 3 && <BookACall ref={bookACallRef} profile={profile} />}
      {stepperStep === 4 && onProceedToDemo && (
        <SetupSuccessStep onProceedToDemo={onProceedToDemo} />
      )}
    </>
  );
}
